import { CREDIT_SCALE } from '../../data/home';
import type { AuthUser } from '../../auth/storage';

function asNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function pickAmount(source: Record<string, unknown>, keys: string[]) {
    for (const key of keys) {
        const amount = asNumber(source[key]);
        if (amount == null) continue;
        return /units/i.test(key) ? amount / CREDIT_SCALE : amount;
    }

    return null;
}

function pickAmountFrom(sources: Record<string, unknown>[], keys: string[]) {
    for (const source of sources) {
        const amount = pickAmount(source, keys);
        if (amount != null) return amount;
    }

    return 0;
}

export function readWalletAmounts(user: AuthUser | null) {
    const wallet =
        user?.wallet && typeof user.wallet === 'object' && !Array.isArray(user.wallet)
            ? (user.wallet as Record<string, unknown>)
            : {};
    const nestedBalance =
        wallet.balance && typeof wallet.balance === 'object' && !Array.isArray(wallet.balance)
            ? (wallet.balance as Record<string, unknown>)
            : null;
    const sources = nestedBalance ? [nestedBalance, wallet] : [wallet];

    return {
        availableCredits: pickAmountFrom(sources, [
            'availableCreditUnits',
            'availableCredits',
            'available',
            'balance',
            'credits',
        ]),
        reservedCredits: pickAmountFrom(sources, [
            'reservedCreditUnits',
            'reservedCredits',
            'reserved',
        ]),
        pendingUsdt: pickAmountFrom(sources, [
            'pendingUsdtUnits',
            'pendingDeposits',
            'pendingUsdt',
            'pending',
        ]),
    };
}

const AVAILABLE_KEYS = ['availableCreditUnits', 'availableCredits', 'available', 'balance', 'credits'];

export function debitAvailableCredits(user: AuthUser, debit: number): AuthUser {
    const amount = Math.round(Math.max(0, debit) * 100) / 100;
    if (amount <= 0) return user;

    const wallet =
        user.wallet && typeof user.wallet === 'object' && !Array.isArray(user.wallet)
            ? { ...(user.wallet as Record<string, unknown>) }
            : {};
    const nestedBalance =
        wallet.balance && typeof wallet.balance === 'object' && !Array.isArray(wallet.balance)
            ? { ...(wallet.balance as Record<string, unknown>) }
            : null;
    const target = nestedBalance ?? wallet;
    let written = false;

    for (const key of AVAILABLE_KEYS) {
        const current = asNumber(target[key]);
        if (current == null) continue;
        const next = Math.max(0, current - (/units/i.test(key) ? amount * CREDIT_SCALE : amount));
        target[key] = typeof target[key] === 'string' ? next.toFixed(2) : next;
        written = true;
        break;
    }

    if (!written) {
        target.availableCredits = Math.max(0, readWalletAmounts(user).availableCredits - amount);
    }
    if (nestedBalance) wallet.balance = nestedBalance;

    return { ...user, wallet };
}
